import React from 'react';
import { ChevronRightIcon, CheckIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import type { ProjectPhase, Project } from '@/features/Projects';
import { 
  PHASE_CONFIG, 
  getPhaseStatus,
  isPhaseAccessible 
} from '../../projectDetails.definitions';

interface ProjectPhaseNavigationProps {
  project: Project;
  currentPhase: ProjectPhase;
  onPhaseChange: (phase: ProjectPhase) => void;
}

export default function ProjectPhaseNavigation({ 
  project, 
  currentPhase, 
  onPhaseChange 
}: ProjectPhaseNavigationProps) {
  // Ordenar fases pela ordem
  const sortedPhases = Object.entries(PHASE_CONFIG)
    .sort(([, a], [, b]) => a.order - b.order)
    .map(([phase]) => phase as ProjectPhase);

  const handlePhaseClick = (phase: ProjectPhase) => {
    if (isPhaseAccessible(phase, currentPhase, project)) {
      onPhaseChange(phase);
    }
  };

  return (
    <div className="bg-white border-b border-gray-200 px-6 py-4 mb-6">
      <div className="max-w-7xl mx-auto">
        {/* Título */}
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Progresso do Projeto
          </h2>
          <p className="text-sm text-gray-500">
            Navegue pelas fases do desenvolvimento do projeto
          </p>
        </div>

        {/* Breadcrumb Navigation */}
        <nav className="flex flex-wrap items-center gap-2 overflow-x-auto pb-2">
          {sortedPhases.map((phase, index) => {
            const config = PHASE_CONFIG[phase];
            const status = getPhaseStatus(phase, currentPhase, project);
            const isAccessible = isPhaseAccessible(phase, currentPhase, project);
            const Icon = config.icon;
            
            return (
              <React.Fragment key={phase}>
                <div
                  className={clsx(
                    'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 min-w-fit',
                    {
                      // Status atual
                      'bg-blue-100 text-blue-800 border-2 border-blue-300 shadow-sm': 
                        status === 'current',
                      
                      // Fase completada
                      'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100': 
                        status === 'completed' && isAccessible,
                      
                      // Fase disponível
                      'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100': 
                        status === 'available' && isAccessible,
                      
                      // Fase bloqueada
                      'bg-gray-50 text-gray-400 border border-gray-200 cursor-not-allowed': 
                        status === 'locked' || !isAccessible,
                        
                      // Hover para fases acessíveis
                      'cursor-pointer hover:shadow-sm': isAccessible,
                    }
                  )}
                  onClick={() => handlePhaseClick(phase)}
                  role={isAccessible ? "button" : undefined}
                  tabIndex={isAccessible ? 0 : -1}
                  title={
                    isAccessible 
                      ? `Ir para ${config.title}` 
                      : `${config.title} - Não disponível ainda`
                  }
                >
                  {/* Status Icon */}
                  <div className="flex-shrink-0">
                    {status === 'completed' ? (
                      <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                        <CheckIcon className="w-3 h-3 text-white" />
                      </div>
                    ) : status === 'current' ? (
                      <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <Icon className="w-3 h-3 text-white" />
                      </div>
                    ) : status === 'available' ? (
                      <Icon className="w-5 h-5 text-gray-500" />
                    ) : (
                      <LockClosedIcon className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                  
                  {/* Phase Title */}
                  <span className="font-medium whitespace-nowrap">
                    {config.title}
                  </span>
                  
                  {/* Progress indicator */}
                  {status === 'current' && (
                    <div className="ml-2 w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                  )}
                </div>
                
                {/* Separator Arrow */}
                {index < sortedPhases.length - 1 && (
                  <ChevronRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                )}
              </React.Fragment>
            );
          })}
        </nav>

        {/* Phase Description */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-1">
              {React.createElement(PHASE_CONFIG[currentPhase].icon, {
                className: `w-5 h-5 text-${PHASE_CONFIG[currentPhase].color}-600`
              })}
            </div>
            <div>
              <h3 className="font-medium text-gray-900">
                {PHASE_CONFIG[currentPhase].title}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {PHASE_CONFIG[currentPhase].description}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}